import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { FlaskConical, CalendarDays } from 'lucide-react';
import DashboardTabNav from '@/components/polymer/DashboardTabNav';
import { useTheme } from '@/contexts/ThemeContext';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';

interface Props {
  config: PolymerDashboardConfig;
  renderContent: (activeTab: string) => React.ReactNode;
}

const PolymerDashboardLayout: React.FC<Props> = ({ config, renderContent }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark } = useTheme();
  const activeTab = searchParams.get('tab') || config.defaultTab;

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-rs-black" data-theme={isDark ? undefined : 'light'}>
      <main className="flex flex-col">
        <div className="sticky top-0 z-20 bg-rs-off-black/80 border-b border-rs-jet-light/50 px-6 py-5 backdrop-blur-sm">
          <div className="mb-1 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-light text-rs-off-white flex items-center">
                <FlaskConical size={22} className="inline-block text-rs-green/70 mr-2 align-middle" />
                {config.polymer}
                <span className="ml-2 text-rs-off-white/40 text-xl">({config.polymerFullName})</span>
              </h1>
              <p className="text-rs-off-white/50 text-sm mt-0.5">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays size={13} className="opacity-40" />
                  {dateLabel}
                </span>
              </p>
            </div>
          </div>
          <div className="mt-4">
            <DashboardTabNav
              tabs={config.tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>
        </div>
        <div className="p-6">{renderContent(activeTab)}</div>
      </main>
    </div>
  );
};

export default PolymerDashboardLayout;
